import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { createContext, useCallback, useContext, useEffect, useReducer, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ActionType, KeyboardEventKey } from './types';
export function reducer(state, action) {
  switch (action.type) {
    // Registers an element to be tracked in the state
    case ActionType.Register:
      {
        var itemToRegister = action.payload;
        if (state.focusItems.some(function (item) {
          return item.id === itemToRegister.id;
        })) {
          return state;
        }
        var items = state.focusItems.slice();
        items.splice(itemToRegister.index, 0, itemToRegister);
        return _objectSpread(_objectSpread({}, state), {}, {
          focusItems: items,
          selectedItem: state.selectedItem === null ? itemToRegister.id : state.selectedItem
        });
      }

    // Remove element from state if they are no longer rendered in the list.
    case ActionType.Unregister:
      {
        var id = action.payload;
        var itemIndex = state.focusItems.findIndex(function (item) {
          return item.id === state.selectedItem;
        });
        var newItems = state.focusItems.filter(function (item) {
          return item.id !== id;
        });
        if (newItems.length === 0) {
          return {
            selectedItem: null,
            focusItems: [],
            active: false
          };
        }

        // Unregister should occur when an element is no longer visible
        // If its the current item, selected should move to the next item.
        var isCurrentStop = id === state.selectedItem;
        var selectedItem = state.selectedItem;

        // If the item removed was last in the list -> apply focus to new last
        if (newItems.length === itemIndex) {
          var _newItems;
          selectedItem = (_newItems = newItems[newItems.length - 1]) === null || _newItems === void 0 ? void 0 : _newItems.id;
        }
        // If the removed element was currently the selected element, new selected is next item
        else if (isCurrentStop) {
          var _newItems$itemIndex;
          selectedItem = (_newItems$itemIndex = newItems[itemIndex]) === null || _newItems$itemIndex === void 0 ? void 0 : _newItems$itemIndex.id;
        }
        return _objectSpread(_objectSpread({}, state), {}, {
          focusItems: newItems,
          selectedItem: selectedItem
        });
      }

    // Position of items should be updated if their tab index changes.
    // For example, if an notification is marked as read and the unread filter is on.
    case ActionType.UpdateIndex:
      {
        var itemToUpdate = action.payload;
        var oldIndex = state.focusItems.findIndex(function (item) {
          return item.id === itemToUpdate.id;
        });
        if (oldIndex === -1 || state.focusItems[oldIndex].index === itemToUpdate.index) {
          return state;
        }
        var _items = state.focusItems.slice();
        // The element will be in the correct position because items
        // are inserted in the correct order. Replace with the latest index.
        _items[oldIndex] = itemToUpdate;
        return _objectSpread(_objectSpread({}, state), {}, {
          focusItems: _items
        });
      }

    // Go to the next focusable list element.
    // If the current item is the last in the list, wrap to the start of the list.
    case ActionType.NextRow:
      {
        var _state$focusItems;
        var index = state.focusItems.findIndex(function (item) {
          return item.id === state.selectedItem;
        });
        if (index === -1) {
          return state;
        }
        return _objectSpread(_objectSpread({}, state), {}, {
          selectedItem: (_state$focusItems = state.focusItems[(index + 1) % state.focusItems.length]) === null || _state$focusItems === void 0 ? void 0 : _state$focusItems.id
        });
      }

    // Go to the previous focusable list element.
    // If the current item is the first in the list, wrap to the end of the list.
    case ActionType.PreviousRow:
      {
        var _state$focusItems2;
        var _index = state.focusItems.findIndex(function (item) {
          return item.id === state.selectedItem;
        });
        if (_index === -1) {
          return state;
        }
        return _objectSpread(_objectSpread({}, state), {}, {
          selectedItem: (_state$focusItems2 = state.focusItems[(_index - 1 + state.focusItems.length) % state.focusItems.length]) === null || _state$focusItems2 === void 0 ? void 0 : _state$focusItems2.id
        });
      }

    // Focus the last available element
    case ActionType.LastRow:
      {
        var _state$focusItems3;
        if (state.focusItems.length === 0) {
          return _objectSpread(_objectSpread({}, state), {}, {
            selectedItem: null,
            active: false
          });
        }
        return _objectSpread(_objectSpread({}, state), {}, {
          selectedItem: (_state$focusItems3 = state.focusItems[state.focusItems.length - 1]) === null || _state$focusItems3 === void 0 ? void 0 : _state$focusItems3.id
        });
      }

    // Focus the first available element
    case ActionType.FirstRow:
      {
        var _state$focusItems$;
        if (state.focusItems.length === 0) {
          return _objectSpread(_objectSpread({}, state), {}, {
            selectedItem: null,
            active: false
          });
        }
        return _objectSpread(_objectSpread({}, state), {}, {
          selectedItem: (_state$focusItems$ = state.focusItems[0]) === null || _state$focusItems$ === void 0 ? void 0 : _state$focusItems$.id
        });
      }

    // Activate manually focuses the first element available
    case ActionType.Activate:
      {
        var _state$focusItems$2;
        if (state.focusItems.length === 0) {
          return state;
        }
        return _objectSpread(_objectSpread({}, state), {}, {
          selectedItem: (_state$focusItems$2 = state.focusItems[0]) === null || _state$focusItems$2 === void 0 ? void 0 : _state$focusItems$2.id,
          active: true
        });
      }

    // Notifications can be focused throught 'Tab' and should update selectedItem
    // when this happens.
    case ActionType.Focus:
      {
        var _index2 = state.focusItems.findIndex(function (item) {
          return item.id === action.payload;
        });
        if (_index2 === -1) {
          return state;
        }
        return _objectSpread(_objectSpread({}, state), {}, {
          selectedItem: action.payload,
          active: true
        });
      }

    // Blur is triggered when a notification is blurred. These action come after
    // other navigation actions (such as NextRow/PreviousRow). If a notification is blurred
    // through some other way (such as tabbing within a notification, or a click), then the state should be reset.
    case ActionType.Blur:
      {
        if (action.payload !== state.selectedItem) {
          return state;
        }
        return _objectSpread(_objectSpread({}, state), {}, {
          selectedItem: null,
          active: false
        });
      }
  }
}
export var keyboardEventToAction = function keyboardEventToAction(event) {
  switch (event.key) {
    case KeyboardEventKey.ArrowDown:
      {
        // Stop propagation to stop event propagating to document
        // keydown handler and applying focus to first element.
        event.nativeEvent.stopImmediatePropagation();
        return event.shiftKey ? ActionType.LastRow : ActionType.NextRow;
      }
    case KeyboardEventKey.ArrowUp:
      {
        return event.shiftKey ? ActionType.FirstRow : ActionType.PreviousRow;
      }
    default:
      return undefined;
  }
};

/**
 * Component that attaches a keydown handler to the document.
 * Watches for ArrowDown keydown events and trigger activates keyboard navigation.
 */
export function FocusActivator(_ref) {
  var children = _ref.children;
  var _useListFocusProvider = useListFocusProvider(),
    _useListFocusProvider2 = _slicedToArray(_useListFocusProvider, 2),
    dispatch = _useListFocusProvider2[1];
  var keydownListener = useCallback(function (event) {
    if (event.key === KeyboardEventKey.ArrowDown) {
      event.preventDefault(); // stop scroll for initial activation
      dispatch({
        type: ActionType.Activate,
        payload: null
      });
    }
  }, [dispatch]);
  useEffect(function () {
    document.addEventListener('keydown', keydownListener);
    return function () {
      return document.removeEventListener('keydown', keydownListener);
    };
  }, [keydownListener]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, children);
}
export function useFocusManager(ref, index) {
  var _useListFocusProvider3 = useListFocusProvider(),
    _useListFocusProvider4 = _slicedToArray(_useListFocusProvider3, 2),
    state = _useListFocusProvider4[0],
    dispatch = _useListFocusProvider4[1];
  var idRef = useRef(null);
  function getId() {
    if (!idRef.current) {
      idRef.current = uuidv4();
    }
    return idRef.current;
  }
  useEffect(function () {
    dispatch({
      type: ActionType.Register,
      payload: {
        id: getId(),
        ref: ref,
        index: index
      }
    });
    return function () {
      dispatch({
        type: ActionType.Unregister,
        payload: getId()
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(function () {
    dispatch({
      type: ActionType.UpdateIndex,
      payload: {
        id: getId(),
        ref: ref,
        index: index
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);
  var handleKeydown = useCallback(function (event) {
    var type = keyboardEventToAction(event);
    if (type !== undefined) {
      dispatch({
        type: type,
        payload: null
      });
    }
  }, [dispatch]);

  // If an element becomes focused through hitting 'Tab' make sure the selectId is updated.
  var handleOnFocus = useCallback(function () {
    dispatch({
      type: ActionType.Focus,
      payload: getId()
    });
  }, [dispatch]);
  var handleOnBlur = useCallback(function () {
    dispatch({
      type: ActionType.Blur,
      payload: getId()
    });
  }, [dispatch]);
  return {
    focused: state.selectedItem === getId() && state.active,
    handleKeydown: handleKeydown,
    handleOnFocus: handleOnFocus,
    handleOnBlur: handleOnBlur
  };
}
var INITIAL_STATE = {
  selectedItem: null,
  focusItems: [],
  active: false
};
export var ListFocusStateContext = /*#__PURE__*/createContext([INITIAL_STATE, function () {}]);
function useListFocusProvider() {
  return useContext(ListFocusStateContext);
}
export var ListFocusProvider = function ListFocusProvider(_ref2) {
  var children = _ref2.children;
  var context = useReducer(reducer, INITIAL_STATE);
  return /*#__PURE__*/React.createElement(ListFocusStateContext.Provider, {
    value: context
  }, /*#__PURE__*/React.createElement(FocusActivator, null, children));
};